
import * as util from 'util'

import * as big from '../big'
import * as int64 from '../int64'
import * as uuid from '../uuid'

import * as fbe from '../fbe'
import * as core from '../core'
import * as inner from '../inner'

const Big = big.Big // eslint-disable-line
const Int64 = int64.Int64 // eslint-disable-line
const UInt64 = int64.UInt64 // eslint-disable-line
const UUID = uuid.UUID // eslint-disable-line

/**
 * AssetUpdate struct
 */
class AssetUpdate {
  /**
   * Initialize struct
   * @param {!number=} key1
   * @param {!UUID=} key2
   * @param {number=} login
   * @param {UUID=} currencyId
   * @param {number=} actualVersion
   * @param {number=} amount
   * @param {number=} credit
   * @param {number=} locked
   * @constructor
   */
  constructor (key1 = new UInt64(0, 0), key2 = new UUID(), login = null, currencyId = null, actualVersion = null, amount = null, credit = null, locked = null) {
    this.Key1 = key1
    this.Key2 = key2
    this.Login = login
    this.CurrencyId = currencyId
    this.ActualVersion = actualVersion
    this.Amount = amount
    this.Credit = credit
    this.Locked = locked
  }

  /**
   * Copy struct (shallow copy)
   * @this {!AssetUpdate}
   * @param {!AssetUpdate} other Other struct
   * @returns {!AssetUpdate} This struct
   */
  copy (other) {
    if (other.Key1 != null) {
      this.Key1 = UInt64.fromNumber(other.Key1)
    } else {
      this.Key1 = null
    }
    if (other.Key2 != null) {
      this.Key2 = new UUID(other.Key2)
    } else {
      this.Key2 = null
    }
    if (other.Login != null) {
      this.Login = UInt64.fromNumber(other.Login)
    } else {
      this.Login = null
    }
    if (other.CurrencyId != null) {
      this.CurrencyId = new UUID(other.CurrencyId)
    } else {
      this.CurrencyId = null
    }
    if (other.ActualVersion != null) {
      this.ActualVersion = UInt64.fromNumber(other.ActualVersion)
    } else {
      this.ActualVersion = null
    }
    if (other.Amount != null) {
      this.Amount = other.Amount
    } else {
      this.Amount = null
    }
    if (other.Credit != null) {
      this.Credit = other.Credit
    } else {
      this.Credit = null
    }
    if (other.Locked != null) {
      this.Locked = other.Locked
    } else {
      this.Locked = null
    }
    return this
  }

  /**
   * Clone struct (deep clone)
   * @this {!AssetUpdate}
   * @returns {!AssetUpdate} Cloned struct
   */
  clone () {
    // Serialize the struct to the FBE stream
    let writer = new AssetUpdateModel(new fbe.WriteBuffer())
    writer.serialize(this)

    // Deserialize the struct from the FBE stream
    let reader = new AssetUpdateModel(new fbe.ReadBuffer())
    reader.attachBuffer(writer.buffer)
    return reader.deserialize().value
  }

  /**
   * Is this struct equal to other one?
   * @this {!AssetUpdate}
   * @param {!AssetUpdate} other Other struct
   * @returns {boolean} Equal result
   */
  eq (other) {
    if (!(other instanceof AssetUpdate)) {
      throw new TypeError('Instance of AssetUpdate is required!')
    }
    return true
  }

  /**
   * Convert struct to JSON
   * @this {!AssetUpdate}
   * @returns {!object} Struct value for JSON
   */
  toJSON () {
    return {
      Key1: ((this.Key1 != null) ? this.Key1.toNumber() : null),
      Key2: ((this.Key2 != null) ? this.Key2.toString() : null),
      Login: ((this.Login != null) ? this.Login.toNumber() : null),
      CurrencyId: ((this.CurrencyId != null) ? this.CurrencyId.toString() : null),
      ActualVersion: ((this.ActualVersion != null) ? this.ActualVersion.toNumber() : null),
      Amount: ((this.Amount != null) ? this.Amount : null),
      Credit: ((this.Credit != null) ? this.Credit : null),
      Locked: ((this.Locked != null) ? this.Locked : null)
    }
  }

  /**
   * Convert JSON to struct
   * @param {!string} json JSON string
   * @returns {!object} Struct value for JSON
   */
  static fromJSON (json) {
    return AssetUpdate.fromObject(JSON.parse(json))
  }

  /**
   * Create struct from object value
   * @param {!AssetUpdate} other Object value
   * @returns {!AssetUpdate} Created struct
   */
  static fromObject (other) {
    return new AssetUpdate().copy(other)
  }

  /**
   * Get the FBE type
   * @this {!AssetUpdate}
   * @returns {!number} FBE type
   */
  get fbeType () {
    return AssetUpdate.fbeType
  }

  /**
   * Get the FBE type (static)
   * @this {!AssetUpdate}
   * @returns {!number} FBE type
   */
  static get fbeType () {
    return 4167
  }
}

export { AssetUpdate };

/**
 * Fast Binary Encoding AssetUpdate field model
 */
class FieldModelAssetUpdate extends fbe.FieldModel {
  /**
   * Initialize field model with the given buffer and offset
   * @param {!fbe.ReadBuffer|!fbe.WriteBuffer} buffer Buffer
   * @param {!number} offset Offset
   * @constructor
   */
  constructor (buffer, offset) {
    super(buffer, offset)
    this.Key1 = new fbe.FieldModelUInt64(buffer, 4 + 4)
    this.Key2 = new fbe.FieldModelUUID(buffer, this.Key1.fbeOffset + this.Key1.fbeSize)
    this.Login = new fbe.FieldModelOptional(new fbe.FieldModelUInt64(buffer, this.Key2.fbeOffset + this.Key2.fbeSize), buffer, this.Key2.fbeOffset + this.Key2.fbeSize)
    this.CurrencyId = new fbe.FieldModelOptional(new fbe.FieldModelUUID(buffer, this.Login.fbeOffset + this.Login.fbeSize), buffer, this.Login.fbeOffset + this.Login.fbeSize)
    this.ActualVersion = new fbe.FieldModelOptional(new fbe.FieldModelUInt64(buffer, this.CurrencyId.fbeOffset + this.CurrencyId.fbeSize), buffer, this.CurrencyId.fbeOffset + this.CurrencyId.fbeSize)
    this.Amount = new fbe.FieldModelOptional(new fbe.FieldModelDouble(buffer, this.ActualVersion.fbeOffset + this.ActualVersion.fbeSize), buffer, this.ActualVersion.fbeOffset + this.ActualVersion.fbeSize)
    this.Credit = new fbe.FieldModelOptional(new fbe.FieldModelDouble(buffer, this.Amount.fbeOffset + this.Amount.fbeSize), buffer, this.Amount.fbeOffset + this.Amount.fbeSize)
    this.Locked = new fbe.FieldModelOptional(new fbe.FieldModelDouble(buffer, this.Credit.fbeOffset + this.Credit.fbeSize), buffer, this.Credit.fbeOffset + this.Credit.fbeSize)
  }

  /**
   * Get the field size
   * @this {!FieldModelAssetUpdate}
   * @returns {!number} Field size
   */
  get fbeSize () {
    return 4
  }

  /**
   * Get the field body size
   * @this {!FieldModelAssetUpdate}
   * @returns {!number} Field body size
   */
  get fbeBody () {
    return 4 + 4 + this.Key1.fbeSize + this.Key2.fbeSize + this.Login.fbeSize + this.CurrencyId.fbeSize + this.ActualVersion.fbeSize + this.Amount.fbeSize + this.Credit.fbeSize + this.Locked.fbeSize
  }

  /**
   * Get the field extra size
   * @this {!FieldModelAssetUpdate}
   * @returns {!number} Field extra size
   */
  get fbeExtra () {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4) > this._buffer.size)) {
      return 0
    }

    this._buffer.shift(fbeStructOffset)

    let fbeResult = this.fbeBody + this.Key1.fbeExtra + this.Key2.fbeExtra + this.Login.fbeExtra + this.CurrencyId.fbeExtra + this.ActualVersion.fbeExtra + this.Amount.fbeExtra + this.Credit.fbeExtra + this.Locked.fbeExtra

    this._buffer.unshift(fbeStructOffset)

    return fbeResult
  }

  /**
   * Get the field type
   * @this {!FieldModelAssetUpdate}
   * @returns {!number} Field type
   */
  get fbeType () {
    return FieldModelAssetUpdate.fbeType
  }

  /**
   * Get the field type (static)
   * @this {!FieldModelAssetUpdate}
   * @returns {!number} Field type
   */
  static get fbeType () {
    return 4167
  }

  /**
   * Check if the struct value is valid
   * @this {!FieldModelAssetUpdate}
   * @param {!boolean} fbeVerifyType Verify model type flag, defaults is true
   * @returns {!boolean} Field model valid state
   */
  verify (fbeVerifyType = true) {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return true
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4 + 4) > this._buffer.size)) {
      return false
    }

    let fbeStructSize = this.readUInt32(fbeStructOffset)
    if (fbeStructSize < (4 + 4)) {
      return false
    }

    let fbeStructType = this.readUInt32(fbeStructOffset + 4)
    if (fbeVerifyType && (fbeStructType !== this.fbeType)) {
      return false
    }

    this._buffer.shift(fbeStructOffset)
    let fbeResult = this.verifyFields(fbeStructSize)
    this._buffer.unshift(fbeStructOffset)
    return fbeResult
  }

  /**
   * Check if the struct fields are valid
   * @this {!FieldModelAssetUpdate}
   * @param {!number} fbeStructSize FBE struct size
   * @returns {!boolean} Field model valid state
   */
  verifyFields (fbeStructSize) {
    let fbeCurrentSize = 4 + 4

    if ((fbeCurrentSize + this.Key1.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.Key1.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.Key1.fbeSize

    if ((fbeCurrentSize + this.Key2.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.Key2.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.Key2.fbeSize

    if ((fbeCurrentSize + this.Login.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.Login.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.Login.fbeSize

    if ((fbeCurrentSize + this.CurrencyId.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.CurrencyId.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.CurrencyId.fbeSize

    if ((fbeCurrentSize + this.ActualVersion.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.ActualVersion.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.ActualVersion.fbeSize

    if ((fbeCurrentSize + this.Amount.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.Amount.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.Amount.fbeSize

    if ((fbeCurrentSize + this.Credit.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.Credit.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.Credit.fbeSize

    if ((fbeCurrentSize + this.Locked.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.Locked.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.Locked.fbeSize

    return true
  }

  /**
   * Get the struct value (begin phase)
   * @this {!FieldModelAssetUpdate}
   * @returns {!number} Field model begin offset
   */
  getBegin () {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    console.assert((fbeStructOffset > 0) && ((this._buffer.offset + fbeStructOffset + 4 + 4) <= this._buffer.size), 'Model is broken!')
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4 + 4) > this._buffer.size)) {
      return 0
    }

    let fbeStructSize = this.readUInt32(fbeStructOffset)
    console.assert((fbeStructSize >= (4 + 4)), 'Model is broken!')
    if (fbeStructSize < (4 + 4)) {
      return 0
    }

    this._buffer.shift(fbeStructOffset)
    return fbeStructOffset
  }

  /**
   * Get the struct value (end phase)
   * @this {!FieldModelAssetUpdate}
   * @param {!number} fbeBegin Field model begin offset
   */
  getEnd (fbeBegin) {
    this._buffer.unshift(fbeBegin)
  }

  /**
   * Get the struct value
   * @this {!FieldModelAssetUpdate}
   * @param {!AssetUpdate} fbeValue Default value, defaults is new AssetUpdate()
   * @returns {!AssetUpdate} AssetUpdate value
   */
  get (fbeValue = new AssetUpdate()) {
    let fbeBegin = this.getBegin()
    if (fbeBegin === 0) {
      return fbeValue
    }

    let fbeStructSize = this.readUInt32(0)
    this.getFields(fbeValue, fbeStructSize)
    this.getEnd(fbeBegin)
    return fbeValue
  }

  /**
   * Get the struct fields values
   * @this {!FieldModelAssetUpdate}
   * @param {!AssetUpdate} fbeValue AssetUpdate value
   * @param {!number} fbeStructSize Struct size
   */
  getFields (fbeValue, fbeStructSize) {
    let fbeCurrentSize = 4 + 4

    if ((fbeCurrentSize + this.Key1.fbeSize) <= fbeStructSize) {
      fbeValue.Key1 = this.Key1.get()
    } else {
      fbeValue.Key1 = new UInt64(0, 0)
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.Key1.fbeSize

    if ((fbeCurrentSize + this.Key2.fbeSize) <= fbeStructSize) {
      fbeValue.Key2 = this.Key2.get()
    } else {
      fbeValue.Key2 = new UUID()
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.Key2.fbeSize

    if ((fbeCurrentSize + this.Login.fbeSize) <= fbeStructSize) {
      fbeValue.Login = this.Login.get()
    } else {
      fbeValue.Login = null
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.Login.fbeSize

    if ((fbeCurrentSize + this.CurrencyId.fbeSize) <= fbeStructSize) {
      fbeValue.CurrencyId = this.CurrencyId.get()
    } else {
      fbeValue.CurrencyId = null
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.CurrencyId.fbeSize

    if ((fbeCurrentSize + this.ActualVersion.fbeSize) <= fbeStructSize) {
      fbeValue.ActualVersion = this.ActualVersion.get()
    } else {
      fbeValue.ActualVersion = null
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.ActualVersion.fbeSize

    if ((fbeCurrentSize + this.Amount.fbeSize) <= fbeStructSize) {
      fbeValue.Amount = this.Amount.get()
    } else {
      fbeValue.Amount = null
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.Amount.fbeSize

    if ((fbeCurrentSize + this.Credit.fbeSize) <= fbeStructSize) {
      fbeValue.Credit = this.Credit.get()
    } else {
      fbeValue.Credit = null
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.Credit.fbeSize

    if ((fbeCurrentSize + this.Locked.fbeSize) <= fbeStructSize) {
      fbeValue.Locked = this.Locked.get()
    } else {
      fbeValue.Locked = null
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.Locked.fbeSize
  }

  /**
   * Set the struct value (begin phase)
   * @this {!FieldModelAssetUpdate}
   * @returns {!number} Field model begin offset
   */
  setBegin () {
    console.assert(((this._buffer.offset + this.fbeOffset + this.fbeSize) <= this._buffer.size), 'Model is broken!')
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructSize = this.fbeBody
    let fbeStructOffset = this._buffer.allocate(fbeStructSize) - this._buffer.offset
    console.assert((fbeStructOffset > 0) && ((this._buffer.offset + fbeStructOffset + fbeStructSize) <= this._buffer.size), 'Model is broken!')
    if ((fbeStructOffset <= 0) || ((this._buffer.offset + fbeStructOffset + fbeStructSize) > this._buffer.size)) {
      return 0
    }

    this.writeUInt32(this.fbeOffset, fbeStructOffset)
    this.writeUInt32(fbeStructOffset, fbeStructSize)
    this.writeUInt32(fbeStructOffset + 4, this.fbeType)

    this._buffer.shift(fbeStructOffset)
    return fbeStructOffset
  }

  /**
   * Set the struct value (end phase)
   * @this {!FieldModelAssetUpdate}
   * @param {!number} fbeBegin Field model begin offset
   */
  setEnd (fbeBegin) {
    this._buffer.unshift(fbeBegin)
  }

  /**
   * Set the struct value
   * @this {!FieldModelAssetUpdate}
   * @param {!AssetUpdate} fbeValue AssetUpdate value
   */
  set (fbeValue) {
    let fbeBegin = this.setBegin()
    if (fbeBegin === 0) {
      return
    }

    this.setFields(fbeValue)
    this.setEnd(fbeBegin)
  }

  /**
   * Set the struct fields values
   * @this {!FieldModelAssetUpdate}
   * @param {AssetUpdate} fbeValue AssetUpdate value
   */
  setFields (fbeValue) {
    this.Key1.set(fbeValue.Key1)
    this.Key2.set(fbeValue.Key2)
    this.Login.set(fbeValue.Login)
    this.CurrencyId.set(fbeValue.CurrencyId)
    this.ActualVersion.set(fbeValue.ActualVersion)
    this.Amount.set(fbeValue.Amount)
    this.Credit.set(fbeValue.Credit)
    this.Locked.set(fbeValue.Locked)
  }
}

export { FieldModelAssetUpdate };

/**
 * Fast Binary Encoding AssetUpdate model
 */
class AssetUpdateModel extends fbe.Model {
  /**
   * Initialize model with the given buffer
   * @param {!fbe.ReadBuffer|!fbe.WriteBuffer} buffer Read/Write buffer, defaults is new fbe.WriteBuffer()
   * @constructor
   */
  constructor (buffer = new fbe.WriteBuffer()) {
    super(buffer)
    this._model = new FieldModelAssetUpdate(this.buffer, 4)
  }

  /**
   * Get the AssetUpdate model
   * @this {!AssetUpdateModel}
   * @returns {!FieldModelAssetUpdate} model AssetUpdate model
   */
  get model () {
    return this._model
  }

  /**
   * Get the model size
   * @this {!AssetUpdateModel}
   * @returns {!number} Model size
   */
  get fbeSize () {
    return this.model.fbeSize + this.model.fbeExtra
  }

  /**
   * Get the model type
   * @this {!AssetUpdateModel}
   * @returns {!number} Model type
   */
  get fbeType () {
    return AssetUpdateModel.fbeType
  }

  /**
   * Get the model type (static)
   * @this {!AssetUpdateModel}
   * @returns {!number} Model type
   */
  static get fbeType () {
    return FieldModelAssetUpdate.fbeType
  }

  /**
   * Check if the struct value is valid
   * @this {!AssetUpdateModel}
   * @returns {!boolean} Model valid state
   */
  verify () {
    if ((this.buffer.offset + this.model.fbeOffset - 4) > this.buffer.size) {
      return false
    }

    let fbeFullSize = this.readUInt32(this.model.fbeOffset - 4)
    if (fbeFullSize < this.model.fbeSize) {
      return false
    }

    return this.model.verify()
  }

  /**
   * Create a new model (begin phase)
   * @this {!AssetUpdateModel}
   * @returns {!number} Model begin offset
   */
  createBegin () {
    return this.buffer.allocate(4 + this.model.fbeSize)
  }

  /**
   * Create a new model (end phase)
   * @this {!AssetUpdateModel}
   * @param {!number} fbeBegin Model begin offset
   */
  createEnd (fbeBegin) {
    let fbeEnd = this.buffer.size
    let fbeFullSize = fbeEnd - fbeBegin
    this.writeUInt32(this.model.fbeOffset - 4, fbeFullSize)
    return fbeFullSize
  }

  /**
   * Serialize the struct value
   * @this {!AssetUpdateModel}
   * @param {!AssetUpdate} value AssetUpdate value
   * @return {!number} Model begin offset
   */
  serialize (value) {
    let fbeBegin = this.createBegin()
    this.model.set(value)
    return this.createEnd(fbeBegin)
  }

  /**
   * Deserialize the struct value
   * @this {!AssetUpdateModel}
   * @param {!AssetUpdate} value AssetUpdate value, defaults is new AssetUpdate()
   * @return {!object} Deserialized AssetUpdate value and its size
   */
  deserialize (value = new AssetUpdate()) {
    if ((this.buffer.offset + this.model.fbeOffset - 4) > this.buffer.size) {
      return { value: new AssetUpdate(), size: 0 }
    }

    let fbeFullSize = this.readUInt32(this.model.fbeOffset - 4)
    console.assert((fbeFullSize >= this.model.fbeSize), 'Model is broken!')
    if (fbeFullSize < this.model.fbeSize) {
      return { value: new AssetUpdate(), size: 0 }
    }

    this.model.get(value)
    return { value: value, size: fbeFullSize }
  }

  /**
   * Move to the next struct value
   * @this {!AssetUpdateModel}
   * @param {!number} prev Previous AssetUpdate model size
   */
  next (prev) {
    this.model.fbeShift(prev)
  }
}

export { AssetUpdateModel };
